import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import type { Service } from "@/lib/types";
import { money } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export function StudioPage() {
  const { user } = useAuth();
  const [services, setServices] = useState<Service[] | null>(null);

  useEffect(() => {
    (async () => {
      const res = await api.get<{ services: Service[] }>("/api/services/mine");
      setServices(res.services);
    })();
  }, []);

  if (!user?.is_creator) {
    return (
      <Card>
        <CardContent className="p-10 text-center text-sm text-muted-foreground">
          Enable creator mode from Settings to start publishing services.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Studio</h1>
          <p className="text-sm text-muted-foreground">
            Publish services, set revision rules, and manage your commission queue.
          </p>
        </div>
        <Button asChild><Link to="/studio/services/new"><Plus className="mr-1 h-4 w-4" /> New service</Link></Button>
      </div>

      <Tabs defaultValue="services">
        <TabsList>
          <TabsTrigger value="services">Services</TabsTrigger>
          <TabsTrigger value="queue">Queue</TabsTrigger>
        </TabsList>
        <TabsContent value="services" className="mt-4 space-y-3">
          {services === null ? (
            Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20" />)
          ) : services.length === 0 ? (
            <Card>
              <CardContent className="p-10 text-center">
                <p className="text-sm text-muted-foreground">No services yet. Create your first one.</p>
                <Button asChild className="mt-4"><Link to="/studio/services/new">Create service</Link></Button>
              </CardContent>
            </Card>
          ) : (
            services.map(s => (
              <Link
                key={s.id}
                to={`/studio/services/${s.id}`}
                className="flex items-center justify-between gap-4 rounded-lg border bg-card p-4 transition hover:border-foreground/30 hover:shadow-sm"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-medium">{s.title}</p>
                    <Badge variant={s.is_active ? "default" : "secondary"}>
                      {s.is_active ? "Live" : "Hidden"}
                    </Badge>
                    <Badge variant="outline" className="font-normal">
                      {s.quote_mode === "instant" ? "Instant" : "Manual quote"}
                    </Badge>
                  </div>
                  <p className="truncate text-xs text-muted-foreground">
                    /{user.handle}/{s.slug} · {s.delivery_days}d · {s.revision_rounds} revisions
                  </p>
                </div>
                <div className="text-right tabular-nums">{money(s.starting_price_cents, s.currency)}</div>
              </Link>
            ))
          )}
        </TabsContent>
        <TabsContent value="queue" className="mt-4">
          <StudioQueue />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StudioQueue() {
  const [jobs, setJobs] = useState<any[] | null>(null);
  useEffect(() => {
    (async () => {
      const res = await api.get<{ jobs: any[] }>("/api/jobs/mine?as=creator");
      setJobs(res.jobs);
    })();
  }, []);
  if (!jobs) {
    return <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>;
  }
  if (jobs.length === 0) {
    return <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">Your queue is empty.</CardContent></Card>;
  }
  return (
    <div className="space-y-3">
      {jobs.map(j => (
        <Link
          key={j.id}
          to={`/jobs/${j.id}`}
          className="flex items-center justify-between gap-4 rounded-lg border bg-card p-4 transition hover:border-foreground/30"
        >
          <div>
            <p className="font-medium">{j.service_title}</p>
            <p className="text-xs text-muted-foreground">
              from {j.counterparty_name ?? `@${j.counterparty_handle}`} · {j.status.replace(/_/g, " ")}
              {j.queue_position ? ` · #${j.queue_position}` : ""}
            </p>
          </div>
          <div className="text-right tabular-nums">{money(j.quoted_price_cents, j.currency)}</div>
        </Link>
      ))}
    </div>
  );
}
